import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/components/HomePage.vue')
  },
  {
    path: '/search-results',
    name: 'SearchResults',
    component: () => import('@/components/SearchResults.vue'),
    meta: { noPageContainer: true }
  },
  {
    path: '/destination/:slug',
    name: 'DestinationDetail',
    component: () => import('@/components/DestinationDetails.vue'),
    props: route => ({ slug: route.params.slug }),
    meta: { requiresAuth: true, noPageContainer: true }
  },
  {
    path: '/saved-trip/:tripId',
    name: 'SavedTripDetail',
    component: () => import('@/components/DestinationDetails.vue'),
    props: route => ({ slug: route.params.tripId }),
    meta: { requiresAuth: true, noPageContainer: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginPage.vue'),
    meta: { authModal: 'login', noPageContainer: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/LoginPage.vue'),
    meta: { authModal: 'register', noPageContainer: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/LoginPage.vue'),
    meta: { authModal: 'forgot-password', noPageContainer: true }
  },
  {
    path: '/verify-email',
    name: 'EmailVerification',
    component: () => import('@/views/EmailVerification.vue')
  },
  {
    path: '/reset-password',
    name: 'PasswordReset',
    component: () => import('@/views/PasswordReset.vue')
  },
  {
    path: '/budget-estimator',
    name: 'BudgetEstimator',
    component: () => import('@/components/BudgetEstimation.vue')
  },
  {
    path: '/trip-finder',
    name: 'TripFinder',
    component: () => import('@/components/HomePage.vue')
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/components/Profile.vue'),
    meta: { requiresAuth: true, requiresVerification: false }
  },
  // Account Routes - Using AccountLayout wrapper
  {
    path: '/triphub',
    name: 'TripHub',
    component: () => import('@/components/TripHub.vue'),
    meta: { requiresAuth: true, requiresVerification: true, layout: 'account' }
  },
  {
    path: '/rewards',
    name: 'Rewards',
    component: () => import('@/components/Rewards.vue'),
    meta: { requiresAuth: true, requiresVerification: true, layout: 'account' }
  },
  {
    path: '/saved-trips',
    name: 'SavedTrips',
    component: () => import('@/components/SavedTrips.vue'),
    meta: { requiresAuth: true, requiresVerification: true, layout: 'account' }
  },
  {
    path: '/subscription',
    name: 'Subscription',
    component: () => import('@/components/Subscription.vue'),
    meta: { requiresAuth: true, requiresVerification: false, layout: 'account' }
  },
  {
    path: '/community',
    name: 'Community',
    component: () => import('@/components/account/Community.vue'),
    meta: { requiresAuth: true, requiresVerification: true, layout: 'account' }
  },
  {
    path: '/alerts',
    name: 'Alerts',
    component: () => import('@/components/account/Alerts.vue'),
    meta: { requiresAuth: true, requiresVerification: true, layout: 'account' }
  },
  // Admin Routes
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/admin/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('@/views/admin/UserManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/cache',
    name: 'AdminCache',
    component: () => import('@/views/admin/CacheManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/database',
    name: 'AdminDatabase',
    component: () => import('@/views/admin/DatabaseQuery.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, behavior: 'instant' };
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Initialize auth store if not already done
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // redirect  — where to go AFTER successful login
    // returnTo  — where to go if the user CANCELS (i.e. where they came from)
    // Only include returnTo when it's a real app page, not another auth route.
    const isAuthRoute = (path) => ['/login', '/register', '/forgot-password'].includes(path)
    const returnTo = from.fullPath && !isAuthRoute(from.fullPath) ? from.fullPath : null

    next({
      name: 'Login',
      query: { redirect: to.fullPath, ...(returnTo ? { returnTo } : {}) }
    })
  } else if (to.meta.requiresAdmin && !authStore.isAdmin) {
    // Redirect non-admin users trying to access admin pages
    next({ name: 'Home' })
  } else if ((to.name === 'Login' || to.name === 'Register') && authStore.isAuthenticated) {
    // Redirect authenticated users away from auth pages
    next({ name: 'TripHub' })
  } else if (to.meta.requiresVerification && authStore.user && !authStore.user.email_verified) {
    // Check if development bypass is enabled (always true in dev mode)
    const devBypassEnabled = import.meta.env.DEV

    // Redirect unverified users to profile page with message (unless bypass is enabled)
    if (!devBypassEnabled && to.name !== 'Profile') {
      next({
        name: 'Profile',
        query: { unverified: 'true' }
      })
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
